import {useContext, type ReactNode} from 'react';
import {ProfileContext, ProfileProvider} from 'contexts/ProfileContext';
import {isMinimumRole, roleToDisplayString} from 'contexts/profileUtil';
import {Loading} from 'components/loading/Loading';
import {NoAccess} from 'components/errors/NoAccess';
import type {Role} from 'types/role';

interface RequireMinimumRoleProps {
  /** Lowest course role allowed through — e.g. `Role.STUDENT` requires enrollment. */
  minimumRole: Role;
  /** Rendered once the signed-in user meets {@link minimumRole} in the current course. */
  children: ReactNode;
}

/** Props for {@link RequireMinimumRoleContent}. */
interface RequireMinimumRoleContentProps extends RequireMinimumRoleProps {
  /**
   * Shown on the no-access page. The caller translates it. Omitted: the
   * no-access page's own translated message.
   */
  message?: ReactNode;
}

/**
 * Gates `children` behind a minimum course role, for routes mounted outside
 * `CoursePage` (which would otherwise provide this for free) — namely
 * `/:courseId/tools/*`. Each tool declares its own `minimumRole` at the
 * route level (see `PaiToolRoutes.tsx`), rather than one blanket role
 * applying to every tool.
 *
 * Provides its own `ProfileProvider` since tool routes sit outside
 * `CoursePage`'s tree and don't otherwise have one. Must be rendered under a
 * route with a `:courseId` param and an already-authenticated user (see
 * `ToolRoutesSlot`, which wraps its `ToolRoutes` override in `Authenticated`
 * before this ever renders).
 */
export function RequireMinimumRole({
  minimumRole,
  children,
}: RequireMinimumRoleProps) {
  return (
    <ProfileProvider>
      <RequireMinimumRoleContent
        minimumRole={minimumRole}
        message={`You need to be at least a ${roleToDisplayString(minimumRole)} in this course to use this tool.`}
      >
        {children}
      </RequireMinimumRoleContent>
    </ProfileProvider>
  );
}

/**
 * Gates `children` behind a minimum course role, reading the profile from the
 * nearest `ProfileProvider`. Use it directly for a route inside `CoursePage`,
 * which already provides one, so the role is declared on the route
 * (see [#5241](https://github.com/CodeinPlace/cip/issues/5241)). `children`
 * never mount for a viewer below the role, so they fetch nothing.
 *
 * @param props - See {@link RequireMinimumRoleContentProps}.
 * @return A loading indicator while the profile loads, the no-access page for
 *     a viewer below `minimumRole`, and `children` otherwise.
 * @throws Never.
 */
export function RequireMinimumRoleContent({
  minimumRole,
  children,
  message,
}: RequireMinimumRoleContentProps) {
  const {userData, loading} = useContext(ProfileContext);

  if (loading) {
    return <Loading />;
  }
  if (!userData || !isMinimumRole(userData.courseRole, minimumRole)) {
    return <NoAccess message={message} />;
  }
  return <>{children}</>;
}
